import { createAsyncThunk } from '@reduxjs/toolkit'
import { apiClient } from './apiClient'
import type { Query } from './apiTypes'
import { useStore } from '@/store'
import type { SearchSliceEnvelope } from './searchContract'
import type { WorldFlowEnvelope } from './worldFlowTypes'

function asEnvelope(data: unknown): SearchSliceEnvelope {
  const d = data as Record<string, unknown>
  return {
    ts: Number(d.ts ?? Date.now()),
    fromMs: Number(d.fromMs ?? 0),
    toMs: Number(d.toMs ?? 0),
    dimension: typeof d.dimension === 'string' ? d.dimension : undefined,
    view:
      d.view === 'stream' || d.view === 'dimension' || d.view === 'analysis'
        ? (d.view as 'stream' | 'dimension' | 'analysis')
        : undefined,
    slice:
      d.slice && typeof d.slice === 'object'
        ? (d.slice as { type: string; value: string })
        : undefined,
    nodes: Array.isArray(d.nodes) ? (d.nodes as SearchSliceEnvelope['nodes']) : [],
    critical: typeof d.critical === 'number' ? d.critical : undefined,
    warnings: typeof d.warnings === 'number' ? d.warnings : undefined,
    total_streams: typeof d.total_streams === 'number' ? d.total_streams : undefined,
    total_users: typeof d.total_users === 'number' ? d.total_users : undefined,
  }
}

/**
 * Single REST-fetch thunk that handles every search view. The `query.view`
 * field picks which endpoint we hit and where the result lands in the store.
 */
export const fetchSearch = createAsyncThunk<
  { key: string; envelope: SearchSliceEnvelope },
  { key: string; query: Query; scope?: 'list' | 'drill' }
>('search/fetch', async ({ key, query, scope }, thunkApi) => {
  if (query.view === 'world') {
    const data = await apiClient.worldFlow(
      {
        time: query.time,
        filters: query.filters,
        clientGrain: query.clientGrain,
        includeOffline: query.includeOffline,
      },
      thunkApi.signal,
    )
    const flow: WorldFlowEnvelope = {
      ts: data.ts ?? Date.now(),
      fromMs: data.fromMs,
      toMs: data.toMs,
      hops: data.hops ?? [],
      links: data.links ?? [],
      regionSeverity: data.regionSeverity ?? {},
    }
    useStore.setState({
      lastDataError: null,
      worldFlowResult: flow,
      lastStreamsUpdateTs: Date.now(),
      restReadyKey: key,
    })
    return { key, envelope: asEnvelope({ ...flow, nodes: [] }) }
  }

  const data =
    query.view === 'stream'
      ? await apiClient.streamSearch(query, thunkApi.signal)
      : query.view === 'analysis'
        ? await apiClient.analysisSearch(query, thunkApi.signal)
        : await apiClient.dimensionSearch(query, thunkApi.signal)

  const envelope = asEnvelope(data)
  envelope.view = query.view
  if (query.view === 'dimension' || query.view === 'analysis') {
    envelope.dimension = query.dimension
  }
  envelope.slice = query.slice

  const patch: Record<string, unknown> = { lastDataError: null, restReadyKey: key }
  if (scope === 'drill') patch.searchResultDrill = envelope
  else patch.searchResult = envelope
  if (query.view === 'stream') patch.lastStreamsUpdateTs = Date.now()
  useStore.setState(patch)

  return { key, envelope }
})

/** @deprecated use fetchSearch with `query.view = 'dimension'`. */
export const fetchDimensionSearch = fetchSearch
/** @deprecated use fetchSearch with `query.view = 'stream'`. */
export const fetchStreamSearch = fetchSearch
